import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Avatar,
  Paper,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import DeleteIcon from "@mui/icons-material/Delete";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { fetchGateVisitorTypes, fetchGateVisitorStatuses } from "./Utils/gateVisitors.api";
import api, { API_BASE_URL } from "../../utils/api";
import GateVisitorDetailsModal from "./GateVisitorDetailsModal";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import BadgeIcon from "@mui/icons-material/Badge";
import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import GroupsIcon from "@mui/icons-material/Groups";

const GateVisitorsCards = ({ visits = [], getStatusDetails, onRefresh }) => {
  const [visitorTypes, setVisitorTypes] = useState([]);
  const [erroredImages, setErroredImages] = useState({});
  const [openImageModalId, setOpenImageModalId] = useState(null);
  const [membersMap, setMembersMap] = useState({});
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [visitorStatuses, setVisitorStatuses] = useState([]);
  const [saveError, setSaveError] = useState("");
  const [actionSnack, setActionSnack] = useState({ open:false, msg:"", severity:"success" });

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [typesRes, statusesRes] = await Promise.all([
          fetchGateVisitorTypes(),
          fetchGateVisitorStatuses(),
        ]);
        const types = typesRes?.data || typesRes || [];
        const statuses = statusesRes?.data || statusesRes || [];
        setVisitorTypes(types);
        setVisitorStatuses(statuses);
      } catch (error) {
        console.error("Error loading dropdown data:", error);
      }
    };

    loadDropdownData();
  }, []);

  useEffect(() => {
    const fetchMember = async (clubMemberID) => {
      try {
        const res = await api.get(`/members/${encodeURIComponent(clubMemberID)}`);
        return res.data;
      } catch (err) {
        console.error(`Error loading member info for ID ${clubMemberID}:`, err);
        return null;
      }
    };

    const loadMembers = async () => {
      const ids = Array.from(new Set(visits.map((v) => v.ClubMemberID).filter(Boolean)));
      const map = {};

      const results = await Promise.allSettled(
        ids.map((id) => fetchMember(id).then((member) => ({ id, member })))
      );
      results.forEach((r) => {
        if (r.status === "fulfilled" && r.value.member) {
          map[r.value.id] = r.value.member;
        }
      });

      setMembersMap(map);
    };

    if (visits.length) loadMembers();
  }, [visits]);

  const handleEditChange = (field, value) => {
    setSelectedVisitor((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveVisitor = async () => {
    setSaveError("");
    try {
      const payload = {
        ...selectedVisitor,
        AuthorizedBy: selectedVisitor.AuthorizedBy || "System",
        EnteredBy: selectedVisitor.EnteredBy || "System",
      };
      await api.put(`/gate-visitors/${selectedVisitor.VisitID}`, payload);
      setSelectedVisitor(null);
      onRefresh?.(); // refresh the list on success
    } catch (err) {
      console.error("Failed to save visitor", err);
      setSaveError(err.response?.data?.error || "Failed to save changes. Please try again.");
      // Don't close the modal on error so the user can retry
    }
  };

  const showSnack = (msg, severity = "success") => setActionSnack({ open:true, msg, severity });

  const handleCheckIn = async (visitId) => {
    try {
      await api.patch(`/gate-visitors/${visitId}/checkin`);
      showSnack("Visitor checked in.");
      onRefresh?.();
    } catch (err) {
      showSnack(err.response?.data?.error || "Check-in failed.", "error");
    }
  };

  const handleCheckOut = async (visitId) => {
    try {
      await api.patch(`/gate-visitors/${visitId}/checkout`);
      showSnack("Visitor checked out.");
      onRefresh?.();
    } catch (err) {
      showSnack(err.response?.data?.error || "Check-out failed.", "error");
    }
  };

  const handleDelete = async (visitId) => {
    if (!window.confirm("Delete this visitor record? This cannot be undone.")) return;
    try {
      await api.delete(`/gate-visitors/${visitId}`);
      showSnack("Visitor record deleted.");
      onRefresh?.();
    } catch (err) {
      showSnack("Delete failed.", "error");
    }
  };

  const handleNotifyMember = async (visitId) => {
    try {
      await api.post(`/gate-visitors/${visitId}/notify-member`);
      showSnack("Member notified by email.");
    } catch (err) {
      showSnack(err.response?.data?.error || "Notification failed.", "error");
    }
  };

  const getVisitorTypeName = (typeId) => {
    const found = visitorTypes.find((t) => Number(t.TypeID) === Number(typeId));
    return found?.TypeName || "Unknown Type";
  };

  const handleImageError = (visitId) => {
    setErroredImages((prev) => ({ ...prev, [visitId]: true }));
  };

  if (!Array.isArray(visits) || visits.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "16px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          backgroundColor: "rgba(255,255,255,0.96)",
          textAlign: "center",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No gate visitors found.
        </Typography>
      </Paper>
    );
  }

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2.5}>
        {visits.map((v) => {
          const statusName = v.StatusName || "Unknown";
          const { color = "grey", Icon = null, label = statusName } =
            getStatusDetails?.(statusName) || {};

          const initials = `${v.FirstName?.[0] || ""}${v.LastName?.[0] || ""}`.toUpperCase();
          const photoUrl = v.PicturePath
            ? `${API_BASE_URL}/gate-visitor-images/${v.PicturePath}`
            : null;
          const showInitials = !photoUrl || erroredImages[v.VisitID];
          const member = membersMap[String(v.ClubMemberID).trim()];

          return (
            <Grid item xs={12} sm={6} lg={4} key={v.VisitID}>
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "stretch",
                  borderRadius: "18px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                  height: "100%",
                  backgroundColor: "rgba(255,255,255,0.96)",
                }}
              >
                <Box
                  sx={{
                    width: 132,
                    minWidth: 132,
                    background:
                      "linear-gradient(180deg, rgba(47,99,173,0.05), rgba(47,99,173,0.02))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: !showInitials ? "pointer" : "default",
                    borderRight: "1px solid rgba(47,99,173,0.08)",
                    overflow: "hidden",
                  }}
                  onClick={!showInitials ? () => setOpenImageModalId(v.VisitID) : undefined}
                >
                  {showInitials ? (
                    <Avatar
                      sx={{
                        width: 78,
                        height: 78,
                        fontSize: "1.7rem",
                        fontWeight: 700,
                        backgroundColor: "#2f63ad",
                        boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                      }}
                    >
                      {initials}
                    </Avatar>
                  ) : (
                    <img
                      src={photoUrl}
                      alt="Visitor"
                      onError={() => handleImageError(v.VisitID)}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </Box>

                <CardContent
                  sx={{
                    flex: 1,
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: "#223142",
                      mb: 1.25,
                    }}
                  >
                    Visit #{v.VisitID}
                  </Typography>

                  <Box display="flex" alignItems="flex-start" mb={0.75}>
                    <PersonIcon fontSize="small" sx={{ mr: 1, mt: "2px", color: "#2f63ad" }} />
                    <Typography variant="body2" sx={{ color: "#223142" }}>
                      <strong>Visitor:</strong>{" "}
                      {`${v.FirstName || ""} ${v.LastName || ""}`.trim() || "N/A"}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="flex-start" mb={0.75}>
                    <EventIcon fontSize="small" sx={{ mr: 1, mt: "2px", color: "#2f63ad" }} />
                    <Typography variant="body2" sx={{ color: "#223142" }}>
                      <strong>Arrival:</strong>{" "}
                      {v.ArrivalDate ? new Date(v.ArrivalDate).toLocaleString() : "N/A"}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="flex-start" mb={0.75}>
                    <ScheduleIcon fontSize="small" sx={{ mr: 1, mt: "2px", color: "#2f63ad" }} />
                    <Typography variant="body2" sx={{ color: "#223142" }}>
                      <strong>Departure:</strong>{" "}
                      {v.DepartureDate ? new Date(v.DepartureDate).toLocaleString() : "N/A"}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="flex-start" mb={0.75}>
                    <BadgeIcon fontSize="small" sx={{ mr: 1, mt: "2px", color: "#2f63ad" }} />
                    <Typography variant="body2" sx={{ color: "#223142" }}>
                      <strong>Member ID:</strong> {v.ClubMemberID || "N/A"}
                    </Typography>
                  </Box>

                  {member ? (
                    <>
                      <Box display="flex" alignItems="flex-start" mb={0.75}>
                        <PersonIcon
                          fontSize="small"
                          sx={{ mr: 1, mt: "2px", color: "#2f63ad" }}
                        />
                        <Typography variant="body2" sx={{ color: "#223142" }}>
                          <strong>Member:</strong> {member.FirstName} {member.LastName}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="flex-start" mb={1}>
                        <HomeIcon fontSize="small" sx={{ mr: 1, mt: "2px", color: "#2f63ad" }} />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#223142",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          <strong>Address:</strong> {member.FullAddress}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 1 }}>
                      Member info not found.
                    </Typography>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Chip
                      icon={<GroupsIcon sx={{ color: "#fff !important", fontSize: 18 }} />}
                      label={getVisitorTypeName(v.VisitorTypeID)}
                      size="small"
                      sx={{
                        backgroundColor: "#2f63ad",
                        color: "white",
                        fontWeight: 700,
                        borderRadius: "8px",
                      }}
                    />

                    <Chip
                      icon={Icon ? <Icon sx={{ color: "#fff !important", fontSize: 18 }} /> : null}
                      label={String(label).toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: (theme) => theme.palette[color]?.main || color,
                        color: "#fff",
                        fontWeight: 700,
                        borderRadius: "8px",
                      }}
                    />
                  </Box>

                  <Box sx={{ mt: "auto", display:"flex", flexDirection:"column", gap:0.75 }}>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      sx={primaryButtonSx}
                      onClick={() => setSelectedVisitor(v)}
                    >
                      View / Edit
                    </Button>
                    <Box sx={{ display:"flex", gap:0.5 }}>
                      <Tooltip title="Quick Check-In">
                        <IconButton size="small"
                          sx={{ flex:1, borderRadius:"8px", bgcolor:"rgba(15,118,110,0.08)", "&:hover":{ bgcolor:"rgba(15,118,110,0.16)" } }}
                          onClick={()=>handleCheckIn(v.VisitID)}>
                          <LoginIcon fontSize="small" sx={{ color:"#0f766e" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Quick Check-Out">
                        <IconButton size="small"
                          sx={{ flex:1, borderRadius:"8px", bgcolor:"rgba(180,83,9,0.08)", "&:hover":{ bgcolor:"rgba(180,83,9,0.16)" } }}
                          onClick={()=>handleCheckOut(v.VisitID)}>
                          <LogoutIcon fontSize="small" sx={{ color:"#b45309" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Notify Member by Email">
                        <IconButton size="small"
                          sx={{ flex:1, borderRadius:"8px", bgcolor:"rgba(124,58,237,0.08)", "&:hover":{ bgcolor:"rgba(124,58,237,0.16)" } }}
                          onClick={()=>handleNotifyMember(v.VisitID)}>
                          <NotificationsIcon fontSize="small" sx={{ color:"#7c3aed" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Record">
                        <IconButton size="small"
                          sx={{ flex:1, borderRadius:"8px", bgcolor:"rgba(220,38,38,0.08)", "&:hover":{ bgcolor:"rgba(220,38,38,0.16)" } }}
                          onClick={()=>handleDelete(v.VisitID)}>
                          <DeleteIcon fontSize="small" sx={{ color:"#dc2626" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>

                <Dialog
                  open={openImageModalId === v.VisitID}
                  onClose={() => setOpenImageModalId(null)}
                  maxWidth="md"
                  fullWidth
                  PaperProps={{
                    sx: {
                      borderRadius: "18px",
                      overflow: "hidden",
                    },
                  }}
                >
                  <DialogContent
                    sx={{
                      position: "relative",
                      textAlign: "center",
                      p: 2,
                    }}
                  >
                    <IconButton
                      onClick={() => setOpenImageModalId(null)}
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        backgroundColor: "rgba(255,255,255,0.9)",
                        zIndex: 2,
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,1)",
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>

                    <img
                      src={photoUrl}
                      alt="Visitor"
                      style={{
                        width: "100%",
                        maxHeight: "85vh",
                        objectFit: "contain",
                        borderRadius: "10px",
                      }}
                    />
                  </DialogContent>
                </Dialog>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <GateVisitorDetailsModal
        open={!!selectedVisitor}
        visitor={selectedVisitor}
        onClose={() => { setSelectedVisitor(null); setSaveError(""); }}
        onChange={handleEditChange}
        onSave={handleSaveVisitor}
        visitorTypes={visitorTypes}
        visitorStatuses={visitorStatuses}
        onAfterSave={onRefresh}
        saveError={saveError}
      />

      <Snackbar
        open={actionSnack.open}
        autoHideDuration={3500}
        onClose={()=>setActionSnack(s=>({...s,open:false}))}
        anchorOrigin={{ vertical:"bottom", horizontal:"center" }}
      >
        <Alert severity={actionSnack.severity} variant="filled" sx={{ borderRadius:3 }}>
          {actionSnack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GateVisitorsCards;
